import { useEffect } from "react";
import { IJumpMarksState } from "src/components-mui/common/jump-marks/jump-marks.types";
import { scrollToTarget } from "src/components-mui/common/jump-marks/use-section-navigation";
import { getDecodedUrlHash } from "src/utils/url/url";

const KEEP_URL_ANCHOR_IN_VIEW_MS = 5000;
const USER_SCROLL_INTENT_EVENTS = ["wheel", "touchstart", "keydown", "pointerdown"] as const;

/** Content above a `#section` may still grow after load, and Safari < 27 has no scroll anchoring to compensate. */
interface IKeepAnchorInViewParams {
    anchor: HTMLElement;
    onStop: () => void;
}

const keepAnchorInViewUntilUserTakesOver = ({ anchor, onStop }: IKeepAnchorInViewParams) => {
    const resizeObserver = new ResizeObserver(() => scrollToTarget({ target: anchor, behavior: "auto" }));

    const stop = () => {
        clearTimeout(timeout);
        resizeObserver.disconnect();
        USER_SCROLL_INTENT_EVENTS.forEach(type => window.removeEventListener(type, stop, { capture: true }));
        onStop();
    };

    const timeout = setTimeout(stop, KEEP_URL_ANCHOR_IN_VIEW_MS);
    USER_SCROLL_INTENT_EVENTS.forEach(type => window.addEventListener(type, stop, { capture: true, passive: true }));
    resizeObserver.observe(document.body);

    return stop;
};

interface IUseKeepUrlAnchorInViewParams {
    anchors: ReadonlyMap<string, HTMLElement>;
    state: IJumpMarksState;
}

export const useKeepUrlAnchorInView = ({ anchors, state }: IUseKeepUrlAnchorInViewParams) => {
    useEffect(() => {
        const urlAnchorId = getDecodedUrlHash();
        const urlAnchor = anchors.get(urlAnchorId);

        if (!urlAnchor) {
            return undefined;
        }

        state.urlAnchorId = urlAnchorId;

        return keepAnchorInViewUntilUserTakesOver({
            anchor: urlAnchor,
            onStop: () => {
                state.visibleId = urlAnchorId;
                state.urlAnchorId = null;
            },
        });
    }, [anchors, state]);
};
